import { useCallback, useContext, useState } from 'react';
import type { DropResult } from '@hello-pangea/dnd';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import type { ProjectConferenceTagWithMetadata } from '@trinity/chat/api/interfaces/projects';
import { TagStateContext } from '@trinity/chat-components/contexts/project-tags';
import type { TagItem } from '@trinity/chat-components/contexts/project-tags';
import { ConferenceTagType } from '@trinity/chat/enums';
import * as Table from '../Table';
import * as DragHandle from './Column.DragHandle';
import * as AccordionToggle from './Column.AccordionToggle';
import * as Ordinal from './Column.Ordinal';
import { Cell as ColorCell, Header as ColorHeader, StarCell } from './Column.Color';
import * as Title from './Column.Title';
import * as Category from './Column.Category';
import * as Status from './Column.Status';
import * as Summary from './Column.Summary';
import * as Actions from './Column.Actions';
import { TagsTableEmpty } from './Tags.Table.Empty';
import styles from './styles/TagsTable.module.css';
import { cx } from '@trinity/chat-components/utils';

// ─── Reorder helper ─────────────────────────────────────────────────────────

function reorder<T>(list: T[], from: number, to: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(from, 1);
  if (!removed) {
    throw new Error(`Invalid reorder operation: from ${from} to ${to} in list of length ${list.length}`);
  }
  result.splice(to, 0, removed);
  return result;
}

// ─── Props ──────────────────────────────────────────────────────────────────

export type TagInlineUpdateParams = {
  tagId: number;
  color?: string;
  title?: string;
  includeSummary?: boolean;
  hidden?: boolean;
};

export type ProjectTagsTableProps = {
  /** Build the URL to navigate to when a tag title is clicked */
  getTagDetailUrl: (tagId: number) => string;
  /** Whether to show the actions column. Defaults to true. */
  canManageTags?: boolean;
  /** Called after a DnD reorder with the new ordinal map */
  onReorder?: (ordinalMap: Record<number, number>) => void;
  /** Called for inline cell edits (color, title, includeSummary). Receives the tag id and changed fields. */
  onUpdateTag?: (params: TagInlineUpdateParams) => void;
  /** Render custom menu items inside the actions popper. Receives the tag, a close fn, and whether the tag has children. */
  renderRowActions?: (
    tag: ProjectConferenceTagWithMetadata,
    close: () => void,
    hasChildren: boolean
  ) => React.ReactNode;
  /** Optional override for summary checkbox accent color (for app theming) */
  summaryCheckedColor?: string;
  loading?: boolean;
  loadingStateRowCount?: number;
};

// ─── Main component ─────────────────────────────────────────────────────────

export const ProjectTagsTable = (props: ProjectTagsTableProps) => {
  const context = useContext(TagStateContext);
  if (!context) {
    throw new Error('ProjectTagsTable must be used within a TagStateContext provider');
  }
  const { state } = context;
  const { onReorder, canManageTags = true } = props;

  const onParentDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const reordered = reorder(state.tags, result.source.index, result.destination.index);
      let ordinal = 1;
      const ordinalMap: Record<number, number> = {};
      for (const t of reordered) {
        ordinalMap[t.id] = ordinal++;
        for (const c of t.children) {
          ordinalMap[c.id] = ordinal++;
        }
      }
      onReorder?.(ordinalMap);
    },
    [state.tags, onReorder]
  );

  return (
    <DragDropContext onDragEnd={onParentDragEnd}>
      <Droppable droppableId="root-project-tag-drop">
        {(provided) => (
          <Table.Root
            ref={provided.innerRef}
            {...provided.droppableProps}
            empty={!props.loading && !state.tags.length}
            EmptyComponent={TagsTableEmpty}
            loading={props.loading}
          >
            <Table.Header.THead className={styles.thead}>
              <Table.Header.TR className={styles.tr}>
                <DragHandle.Header />
                <Ordinal.Header />
                <ColorHeader />
                <Title.Header />
                <Category.Header />
                <Status.Header />
                <Summary.Header />
                {canManageTags && <Actions.Header />}
                <AccordionToggle.Header />
              </Table.Header.TR>
            </Table.Header.THead>
            {props.loading ? (
              <LoadingState rows={props.loadingStateRowCount} canManageTags={canManageTags} />
            ) : (
              state.tags.map((tag, i) => (
                <ParentTagRow
                  key={tag.id}
                  tag={tag}
                  index={i}
                  isLast={i === state.tags.length - 1}
                  canManageTags={canManageTags}
                  getTagDetailUrl={props.getTagDetailUrl}
                  onUpdateTag={props.onUpdateTag}
                  onReorder={props.onReorder}
                  renderRowActions={props.renderRowActions}
                  summaryCheckedColor={props.summaryCheckedColor}
                />
              ))
            )}
            <Table.Body.TBody className={styles.tbody}>{provided.placeholder}</Table.Body.TBody>
          </Table.Root>
        )}
      </Droppable>
    </DragDropContext>
  );
};

ProjectTagsTable.displayName = 'ProjectTagsTable';

// ─── Parent row ─────────────────────────────────────────────────────────────

type ParentTagRowProps = {
  tag: TagItem;
  index: number;
  isLast: boolean;
  canManageTags: boolean;
  getTagDetailUrl: (tagId: number) => string;
  onUpdateTag?: (params: TagInlineUpdateParams) => void;
  onReorder?: (ordinalMap: Record<number, number>) => void;
  renderRowActions?: (
    tag: ProjectConferenceTagWithMetadata,
    close: () => void,
    hasChildren: boolean
  ) => React.ReactNode;
  summaryCheckedColor?: string;
};

const ParentTagRow = ({ tag, index, isLast, canManageTags, ...rest }: ParentTagRowProps) => {
  const [childrenOpen, setChildrenOpen] = useState(true);

  const onChildDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      const reordered = reorder(tag.children, result.source.index, result.destination.index);
      let ordinal = tag.ordinal + 1;
      const ordinalMap: Record<number, number> = {};
      for (const c of reordered) {
        ordinalMap[c.id] = ordinal++;
      }
      rest.onReorder?.(ordinalMap);
    },
    [tag, rest]
  );

  return (
    <>
      <Draggable key={tag.id} draggableId={`parent-${tag.id}`} index={index}>
        {(provided) => (
          <Table.Body.TBody className={styles.tbody}>
            <Table.Body.TR
              ref={provided.innerRef}
              {...provided.draggableProps}
              className={cx(
                styles.parent,
                styles.tr,
                tag.hidden ? styles.hidden : undefined,
                isLast && tag.children.length === 0 ? styles.last : undefined
              )}
            >
              <DragHandle.Cell {...provided.dragHandleProps} />
              <TagCells
                tag={tag}
                hasChildren={tag.children.length > 0}
                canManageTags={canManageTags}
                {...rest}
              />
              <AccordionToggle.Cell
                hidden={tag.children.length === 0}
                open={childrenOpen}
                onClick={() => setChildrenOpen((v) => !v)}
              />
            </Table.Body.TR>
          </Table.Body.TBody>
        )}
      </Draggable>
      {childrenOpen && tag.children.length > 0 && (
        <DragDropContext onDragEnd={onChildDragEnd}>
          <Droppable droppableId={`children-${tag.id}`}>
            {(provided) => (
              <Table.Body.TBody className={styles.tbody} ref={provided.innerRef} {...provided.droppableProps}>
                {tag.children.map((child, ci) => (
                  <ChildTagRow
                    key={child.id}
                    tag={child}
                    index={ci}
                    orderable={tag.children.length > 1}
                    isLast={isLast && ci === tag.children.length - 1}
                    canManageTags={canManageTags}
                    {...rest}
                  />
                ))}
                {provided.placeholder}
              </Table.Body.TBody>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </>
  );
};

// ─── Child row ───────────────────────────────────────────────────────────────

type ChildTagRowProps = {
  tag: ProjectConferenceTagWithMetadata;
  index: number;
  orderable: boolean;
  isLast: boolean;
  canManageTags: boolean;
  getTagDetailUrl: (tagId: number) => string;
  onUpdateTag?: (params: TagInlineUpdateParams) => void;
  renderRowActions?: (
    tag: ProjectConferenceTagWithMetadata,
    close: () => void,
    hasChildren: boolean
  ) => React.ReactNode;
  summaryCheckedColor?: string;
};

const ChildTagRow = ({ tag, index, orderable, canManageTags, ...rest }: ChildTagRowProps) => (
  <Draggable draggableId={`child-${tag.id}`} index={index} isDragDisabled={!orderable}>
    {(provided) => (
      <Table.Body.TR
        ref={provided.innerRef}
        {...provided.draggableProps}
        className={cx(
          styles.tr,
          styles.child,
          tag.hidden ? styles.hidden : undefined,
          rest.isLast ? styles.last : undefined
        )}
      >
        <DragHandle.Cell depth={2} hidden={!orderable} {...provided.dragHandleProps} />
        <TagCells tag={tag} hasChildren={false} canManageTags={canManageTags} {...rest} />
        <AccordionToggle.Cell hidden />
      </Table.Body.TR>
    )}
  </Draggable>
);

// ─── Tag cells (shared between parent and child) ─────────────────────────────

type TagCellsProps = {
  tag: ProjectConferenceTagWithMetadata;
  hasChildren: boolean;
  canManageTags: boolean;
  getTagDetailUrl: (tagId: number) => string;
  onUpdateTag?: (params: TagInlineUpdateParams) => void;
  renderRowActions?: (
    tag: ProjectConferenceTagWithMetadata,
    close: () => void,
    hasChildren: boolean
  ) => React.ReactNode;
  summaryCheckedColor?: string;
};

const TagCells = ({
  tag,
  hasChildren,
  canManageTags,
  getTagDetailUrl,
  onUpdateTag,
  renderRowActions,
  summaryCheckedColor,
}: TagCellsProps) => {
  const isGoodQuote = tag.base.typeId === ConferenceTagType.Global && tag.base.name === 'Good Quote';
  const isEditable =
    tag.base.typeId === ConferenceTagType.Project || tag.base.typeId === ConferenceTagType.UserCreated;

  const statusValue = tag.hidden ? 'Hidden' : 'Published';

  return (
    <>
      <Ordinal.Cell value={tag.ordinal} />
      {isGoodQuote ? (
        <StarCell />
      ) : (
        <ColorCell
          editable={isEditable}
          color={tag.base.color}
          onChange={(color) => onUpdateTag?.({ tagId: tag.id, color })}
        />
      )}
      <Title.Cell
        editable={isEditable}
        isValid={(v) => v.length > 3}
        onSubmit={(title) => onUpdateTag?.({ tagId: tag.id, title })}
        value={tag.base.name}
        tagId={tag.id}
        getTagDetailUrl={getTagDetailUrl}
      />
      <Category.Cell value={tag.base.tagCategory} />
      <Status.Cell value={statusValue} />
      <Summary.Cell
        checked={tag.includeSummary}
        onChange={(e) => onUpdateTag?.({ tagId: tag.id, includeSummary: e.target.checked })}
        checkedColor={summaryCheckedColor}
      />
      {canManageTags && (
        <Actions.Cell
          popupId={`project-tags-actions-popper-${tag.id}`}
          renderContent={(close) => (renderRowActions ? renderRowActions(tag, close, hasChildren) : null)}
        />
      )}
    </>
  );
};

type LoadingStateProps = {
  rows?: number;
  canManageTags: boolean;
};

const LoadingState = ({ rows = 25, canManageTags }: LoadingStateProps) => {
  return Array.from({ length: rows }, (_, rowIndex) => (
    <Table.Body.TBody key={rowIndex} className={styles.tbody}>
      <Table.Body.TR className={cx(styles.parent, styles.tr)}>
        <Table.Body.TD style={{ width: '36px', minWidth: '36px' }} />
        <Table.Body.TD loading rowIndex={rowIndex} style={{ width: '48px', minWidth: '48px' }} />
        <Table.Body.TD loading rowIndex={rowIndex} style={{ width: '48px', minWidth: '48px' }} />
        <Table.Body.TD loading rowIndex={rowIndex} style={{ flex: 1 }} />
        <Table.Body.TD loading rowIndex={rowIndex} style={{ width: '140px', minWidth: '140px' }} />
        <Table.Body.TD loading rowIndex={rowIndex} style={{ width: '100px', minWidth: '100px' }} />
        <Table.Body.TD loading rowIndex={rowIndex} style={{ width: '130px', minWidth: '130px' }} />
        {canManageTags && <Table.Body.TD style={{ width: '36px', minWidth: '36px' }} />}
        <Table.Body.TD style={{ width: '36px', minWidth: '36px' }} />
      </Table.Body.TR>
    </Table.Body.TBody>
  ));
};
